import { cn } from '@listening/utils/react-helpers';
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import * as React from 'react';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.use(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const noUnsubscribe = () => undefined;

const useEmblaSnapshot = <T,>(
  api: CarouselApi | undefined,
  read: (api: NonNullable<CarouselApi>) => T,
  fallback: T,
) =>
  React.useSyncExternalStore(
    (onChange) => {
      if (!api) return noUnsubscribe;
      api.on('select', onChange);
      api.on('reInit', onChange);
      return () => {
        api.off('select', onChange);
        api.off('reInit', onChange);
      };
    },
    () => (api ? read(api) : fallback),
    () => fallback,
  );

const readCurrentStep = (api: NonNullable<CarouselApi>) =>
  api.selectedScrollSnap() + 1;
const readStepCount = (api: NonNullable<CarouselApi>) =>
  api.scrollSnapList().length;
const readCanScrollPrev = (api: NonNullable<CarouselApi>) =>
  api.canScrollPrev();
const readCanScrollNext = (api: NonNullable<CarouselApi>) =>
  api.canScrollNext();

export const useCarouselStep = (api: UseEmblaCarouselType[1] | undefined) => {
  const current = useEmblaSnapshot(api, readCurrentStep, 0);
  const count = useEmblaSnapshot(api, readStepCount, 0);
  return { current, count };
};

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  );
  const canScrollPrev = useEmblaSnapshot(api, readCanScrollPrev, false);
  const canScrollNext = useEmblaSnapshot(api, readCanScrollNext, false);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const { target } = event;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!(api && setApi)) return;
    setApi(api);
  }, [api, setApi]);

  return (
    <CarouselContext
      value={{
        carouselRef,
        api,
        opts,
        orientation:
          orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        aria-roledescription='carousel'
        className={cn('relative', className)}
        data-slot='carousel'
        onKeyDownCapture={handleKeyDown}
        role='region'
        {...props}
      >
        {children}
      </div>
    </CarouselContext>
  );
}

function CarouselContent({
  className,
  wrapperClassName,
  ...props
}: React.ComponentProps<'div'> & {
  wrapperClassName?: string;
}) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      className={cn('overflow-hidden', wrapperClassName)}
      data-slot='carousel-content'
      ref={carouselRef}
    >
      <div
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CarouselItem({
  className,
  ...props
}: React.ComponentProps<'div'> & { inert?: boolean }) {
  const { orientation } = useCarousel();

  return (
    <div
      aria-roledescription='slide'
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      data-slot='carousel-item'
      role='group'
      {...props}
    />
  );
}

export { Carousel, CarouselContent, CarouselItem, type CarouselApi };
